import { jsPDF } from 'jspdf'

const NAVY = [15, 23, 42]
const GOLD = [212, 175, 55]
const CREAM = [252, 250, 245]
const INK = [28, 32, 38]

const ISSUER_LINE = 'iTotal Rewards Pro LLP'
const APPROVER_LINE = 'Manit Mittal'

async function fetchLogoPngBytes() {
  const base = typeof window !== 'undefined' ? window.location.origin : ''
  const url = `${base}/TRP_logo_compressed.png`
  const res = await fetch(url)
  if (!res.ok) throw new Error(`logo fetch ${res.status}`)
  return new Uint8Array(await res.arrayBuffer())
}

/**
 * Landscape A4 certificate — print-ready, suitable for professional sharing.
 */
export async function downloadCertificatePdf({
  certificateId,
  recipientName,
  quizTitle,
  completedDate,
  scoreText,
  trackLabel
}) {
  let logoBytes = null
  try {
    logoBytes = await fetchLogoPngBytes()
  } catch (e) {
    console.warn('certificate: logo omitted', e)
  }

  const doc = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a4' })
  const W = doc.internal.pageSize.getWidth()
  const H = doc.internal.pageSize.getHeight()
  const margin = 14

  doc.setFillColor(...NAVY)
  doc.rect(0, 0, W, H, 'F')

  doc.setDrawColor(...GOLD)
  doc.setLineWidth(0.45)
  doc.rect(margin, margin, W - margin * 2, H - margin * 2, 'S')

  doc.setDrawColor(...GOLD)
  doc.setLineWidth(0.15)
  doc.rect(margin + 2.5, margin + 2.5, W - (margin + 2.5) * 2, H - (margin + 2.5) * 2, 'S')

  const innerL = margin + 6
  const innerT = margin + 6
  const innerW = W - (margin + 6) * 2
  const innerH = H - (margin + 6) * 2

  doc.setFillColor(...CREAM)
  doc.rect(innerL, innerT, innerW, innerH, 'F')

  doc.setDrawColor(220, 215, 205)
  doc.setLineWidth(0.1)
  doc.rect(innerL, innerT, innerW, innerH, 'S')

  const cx = W / 2
  let y = innerT + 10

  if (logoBytes) {
    try {
      const logoW = 42
      const logoH = 14
      doc.addImage(logoBytes, 'PNG', cx - logoW / 2, y, logoW, logoH)
      y += logoH + 5
    } catch (e) {
      console.warn('certificate: addImage failed', e)
      y = innerT + 14
    }
  }

  doc.setTextColor(...GOLD)
  doc.setFontSize(9)
  doc.setFont('helvetica', 'bold')
  doc.text('TOTAL REWARDS PRO', cx, y, { align: 'center' })
  y += 8

  doc.setTextColor(...INK)
  doc.setFontSize(26)
  doc.setFont('helvetica', 'bold')
  doc.text('Certificate of completion', cx, y, { align: 'center' })
  y += 12

  doc.setFontSize(11)
  doc.setFont('helvetica', 'normal')
  doc.setTextColor(70, 78, 90)
  const titleLines = doc.splitTextToSize(String(quizTitle || 'Practice competency assessment'), innerW - 36)
  doc.text(titleLines, cx, y, { align: 'center' })
  y += titleLines.length * 5.2 + 6

  doc.setDrawColor(...GOLD)
  doc.setLineWidth(0.25)
  doc.line(cx - 42, y, cx + 42, y)
  y += 10

  doc.setFontSize(10)
  doc.setTextColor(100, 108, 120)
  doc.text('This is to certify that', cx, y, { align: 'center' })
  y += 10

  doc.setTextColor(...INK)
  doc.setFontSize(20)
  doc.setFont('helvetica', 'bold')
  const name = String(recipientName || 'Participant').trim() || 'Participant'
  doc.text(name, cx, y, { align: 'center' })
  y += 12

  doc.setFont('helvetica', 'normal')
  doc.setFontSize(11)
  doc.setTextColor(60, 68, 78)
  doc.text(
    `has completed the practice competency assessment (${String(trackLabel || 'track')}).`,
    cx,
    y,
    { align: 'center' }
  )
  y += 8

  doc.setFont('helvetica', 'bold')
  doc.setTextColor(...GOLD)
  doc.text(`Score: ${String(scoreText || '—')}`, cx, y, { align: 'center' })
  y += 10

  doc.setFont('helvetica', 'normal')
  doc.setFontSize(10)
  doc.setTextColor(90, 96, 108)
  doc.text(`Date of completion: ${String(completedDate || '')}`, cx, y, { align: 'center' })
  y += 7

  doc.setFontSize(9)
  doc.setTextColor(120, 126, 136)
  doc.text(`Certificate reference: ${String(certificateId || '')}`, cx, y, { align: 'center' })
  y += 14

  doc.setDrawColor(...GOLD)
  doc.setLineWidth(0.2)
  doc.line(innerL + 18, y, cx - 6, y)
  doc.line(cx + 6, y, innerL + innerW - 18, y)
  y += 5

  doc.setFontSize(8)
  doc.setTextColor(70, 76, 88)
  doc.setFont('helvetica', 'normal')
  doc.text('Issuing company', innerL + 18, y)
  doc.text('Approved by', cx + 6, y)
  y += 4.5
  doc.setFont('helvetica', 'bold')
  doc.setTextColor(...INK)
  doc.text(ISSUER_LINE, innerL + 18, y)
  doc.text(APPROVER_LINE, cx + 6, y)

  y = innerT + innerH - 18
  const disclaimer =
    'Issued by iTotal Rewards Pro LLP for professional development only. Not an accredited third-party industry certification. Does not constitute legal, tax, or statutory advice.'
  doc.setFont('helvetica', 'normal')
  doc.setFontSize(7.5)
  doc.setTextColor(130, 130, 138)
  const discLines = doc.splitTextToSize(disclaimer, innerW - 20)
  doc.text(discLines, cx, y, { align: 'center' })

  const safeId = String(certificateId).replace(/[^a-zA-Z0-9-_]/g, '_')
  doc.save(`Total-Rewards-Pro-Certificate-${safeId}.pdf`)
}

export async function downloadCertificateFromAttempt(row) {
  if (row?.passed !== true || !row.certificateId) return
  let completedDate = row.completedDateLabel || ''
  if (!completedDate && row.createdAt && typeof row.createdAt.toDate === 'function') {
    completedDate = row.createdAt.toDate().toLocaleDateString(undefined, {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    })
  }
  await downloadCertificatePdf({
    certificateId: row.certificateId,
    recipientName: row.displayName || 'Participant',
    quizTitle: row.quizTitle || 'Total Rewards competency assessment',
    completedDate,
    scoreText: row.scoreText || `${row.rawScore ?? ''} / ${row.maxPoints ?? ''}`,
    trackLabel: row.trackLabel || row.trackId || ''
  })
}

export function createCertificateId(trackId) {
  const part =
    typeof crypto !== 'undefined' && crypto.randomUUID
      ? crypto.randomUUID().replace(/-/g, '').slice(0, 12).toUpperCase()
      : `${Date.now()}`.slice(-10)
  const tr = String(trackId || 'track').toUpperCase().slice(0, 6)
  return `TRP-${tr}-${part}`
}
